import { ChevronLeft } from "lucide-react";
import RightArrowIcon from "../../assets/svgs/right-arrow.svg";
import Button from "../../components/core/button.core";
import z from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../../components/core/input.core";
import { Form } from "../../../components/ui/form";
import { ImageUploader } from "../../components/ui/image-uploader.ui";
import { useNavigate } from "react-router-dom";
import { useCreateWorkspaceHttp } from "../../hooks/http/colab/workspace/create-workspace.hook";
import { useCreateAssetCommonHttp } from "../../../hooks/http/common/utils/asset/create-asset.common.http";
import { useToast } from "../../../hooks/use-toast";
import { useUserStore } from "../../../store/user.store";
import { useDashboardConfigCommonHttp } from "../../../hooks/http/common/users/dashboard-config.common.http";
import { handleHttpError } from "../../../utils/utils";

// Define the form schema with Zod
const createWorkspaceSchema = z.object({
  coverImageFile: z
    .array(z.instanceof(File))
    .min(1, { message: "Brand logo is required" }),
  coverImage: z
    .object({ id: z.string(), url: z.string() })
    .nullable()
    .default(null),
  brandName: z.string().min(1, { message: "Brand name is required" }),
});

// Infer the type from the schema
type CreateWorkspaceSchema = z.infer<typeof createWorkspaceSchema>;

export default function CreateWorkspacePartial() {
  const { brands, setUser, setBrands } = useUserStore();
  const navigate = useNavigate();
  const { execute: createWorkspaceHttp, fetching: creatingWorkspace } =
    useCreateWorkspaceHttp();
  const { execute: uploadAssetHttp, fetching: uploadingAsset } =
    useCreateAssetCommonHttp();
  const { execute: fetchConfig, fetching: fetchingConfig } =
    useDashboardConfigCommonHttp();

  const { toast } = useToast();

  const form = useForm<CreateWorkspaceSchema>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: {
      coverImageFile: [],
      coverImage: null,
      brandName: "",
    },
  });

  async function fetchConfigData() {
    const response = await fetchConfig({ body: null });
    if (!response.success) {
      return;
    }
    setUser(response.data.user);
    setBrands(response.data.brands);
  }

  async function uploadAsset(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await uploadAssetHttp({
      body: formData,
    });
    if (!response.success) {
      return false;
    }
    return response.data.asset;
  }

  async function onSubmit(data: CreateWorkspaceSchema) {
    console.log(data);
    try {
      let logoId = "";

      // Upload logo if provided
      if (data.coverImageFile.length > 0) {
        const uploadedLogo = await uploadAsset(data.coverImageFile[0]);
        if (!uploadedLogo) {
          return toast({
            content: "Failed to upload logo. Please try again",
            toastType: "error",
          });
        }

        form.setValue("coverImage", {
          id: uploadedLogo.id,
          url: uploadedLogo.url,
        });

        logoId = uploadedLogo.id;
      }

      const response = await createWorkspaceHttp({
        body: {
          name: data.brandName,
          description: null,
          logo: logoId,
        },
      });

      if (!response.success) {
        return handleHttpError(response, toast);
      }

      await fetchConfigData();

      toast({
        content: "Workspace created successfully",
        toastType: "success",
      });

      navigate("/brands/campaigns/live");

      form.reset();
    } catch (error) {
      toast({
        content: "An error occurred while creating workspace",
        toastType: "error",
      });
    }
  }

  return (
    <div className="flex flex-col justify-center items-center p-5 w-full h-screen text-soloprimary">
      <div className="flex flex-col justify-center items-center w-full max-w-lg h-full">
        <div className="flex flex-col items-start bg-soloCardBg px-3 md:px-8 pt-4 md:pt-8 border border-black border-b-0 rounded-b-none rounded-xl w-full h-[60vh] md:h-[90vh] overflow-hidden">
          {brands?.length ? null : (
            <div className="flex items-center gap-3 w-full font-cabinetgrotesk text-sm md:text-xl tracking-tight">
              <Button
                onClick={() => {
                  navigate("/brands/workspace/join");
                }}
                className="p-1"
                variant="secondary"
              >
                <ChevronLeft />
              </Button>
              <div className="pt-1">Join your workspace</div>
            </div>
          )}

          <div className="flex flex-col w-full">
            <p className="pt-5 md:pt-10 pb-12 font-cabinetgrotesk font-normal text-2xl md:text-5xl tracking-tight">
              {brands?.length ? (
                "Workspace created!"
              ) : (
                <span>
                  lets create your brand <br /> workspace
                </span>
              )}
            </p>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-8 pb-9 w-full"
              >
                <div className="flex flex-col gap-8 w-full">
                  <Controller
                    control={form.control}
                    name="coverImageFile"
                    render={({ field, fieldState }) => (
                      <div className="flex flex-col gap-1">
                        <ImageUploader
                          label="Brand Logo"
                          onChange={(files: File[]) => field.onChange(files)}
                          value={field.value}
                          className="flex justify-center items-center border-black w-32 md:w-40 h-32 md:h-40"
                        />
                        {fieldState.error && (
                          <p className="text-red-500 text-sm">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />

                  <div className="flex flex-col gap-3 w-full">
                    <Controller
                      control={form.control}
                      name="brandName"
                      render={({ field, fieldState }) => (
                        <Input
                          ref={field.ref}
                          label="Brand name"
                          placeholder="Your brand name goes here"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          error={fieldState.error?.message}
                          className="py-4"
                          type="text"
                        />
                      )}
                    />
                  </div>
                </div>
              </form>
            </Form>
          </div>
        </div>
        <div className="relative bg-gray-700 pb-1 border border-black rounded-b-2xl w-full overflow-hidden">
          <Button
            onClick={() => {
              form.handleSubmit(onSubmit)();
            }}
            disabled={
              !form.watch("coverImageFile")?.length ||
              !form.watch("brandName") ||
              creatingWorkspace ||
              uploadingAsset ||
              fetchingConfig
            }
            className="border-0 rounded-t-none w-full md:text-lg"
          >
            <p className="pr-3 font-medium">
              {creatingWorkspace || uploadingAsset
                ? "Creating workspace..."
                : brands?.length
                  ? "Go to workspace"
                  : "Create brand workspace"}
            </p>
            <RightArrowIcon />
          </Button>
        </div>
      </div>
    </div>
  );
}
